import { X } from "lucide-react";
import { useState } from "react";

export function Header() {
  const [open, setOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState<string | null>(null);

  const toggleSubmenu = (label: string) => {
    setExpandedSubmenu(expandedSubmenu === label ? null : label);
  };

  const navItems = [
    {
      label: "About us",
      href: "https://ncvic.org/mission",
      submenu: [
        { label: "Our mission", href: "https://ncvic.org/mission" },
        { label: "Team members", href: "https://ncvic.org/team" },
        { label: "Board of directors", href: "https://ncvic.org/board" },
      ],
    },
    { label: "The Issue", href: "https://ncvic.org/the-issue" },
    { label: "Donate", href: "https://ncvic.org/donate" },
    {
      label: "Transparency",
      href: "https://ncvic.org/removals-registry",
      submenu: [
        {
          label: "Removals Registry",
          href: "https://ncvic.org/removals-registry",
        },
        {
          label: "Verification standards",
          href: "https://ncvic.org/verification-standards",
        },
        {
          label: "Oversight board",
          href: "https://ncvic.org/oversight",
        },
      ],
    },
    { label: "Contact", href: "https://ncvic.org/contact" },
  ];

  return (
    <header className="w-full bg-white text-gray-800 border-b border-gray-200 sticky top-0 left-0 z-40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 relative">
          {/* Logo (Left) - Desktop */}
          <div className="hidden lg:flex items-center flex-shrink-0 mr-8">
            <a href="https://ncvic.org/">
              <img
                src="/removals-registry/ncviclogo.png"
                alt="NCVIC"
                className="h-12 w-auto object-contain"
                style={{
                  filter: "brightness(0) saturate(100%)",
                  opacity: 0.85,
                }}
              />
            </a>
          </div>

          {/* Desktop Nav */}
          <div className="flex items-center justify-end gap-8 flex-1">
            <nav className="hidden lg:block">
              <ul className="flex gap-6 text-base font-medium">
                {navItems.map((item) => (
                  <li key={item.label} className="relative group">
                    <a
                      href={item.href}
                      className="group relative inline-block cursor-pointer text-gray-800 hover:text-gray-600 transition-colors"
                    >
                      <span className="inline-block group-hover:opacity-0 transition-opacity">
                        {item.label}
                      </span>
                      <span
                        className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity italic inline-block"
                        style={{
                          fontFamily: '"Times New Roman", Times, serif',
                        }}
                      >
                        {item.label}
                      </span>
                    </a>
                    {item.submenu && (
                      <div className="absolute top-full mt-2 left-0 bg-[#6A98B1] rounded-2xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-[200px] z-100 p-2">
                        <ul className="py-2">
                          {item.submenu.map((subItem, idx) => (
                            <li key={subItem.label}>
                              <a
                                href={subItem.href}
                                className={`block px-6 py-2 text-white font-semibold font-inter rounded-xl transition-colors hover:bg-[#466A87] ${
                                  idx === 0 ? "" : "hover:font-bold"
                                }`}
                                style={{ fontStyle: "normal" }}
                              >
                                {subItem.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Center Logo - Mobile */}
          <a href="https://ncvic.org/" className="lg:hidden absolute left-0">
            <img
              src="/removals-registry/ncviclogo.png"
              alt="NCVIC"
              className="h-10 w-auto object-contain"
              style={{
                filter: "brightness(0) saturate(100%)",
                opacity: 0.85,
              }}
            />
          </a>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-gray-800 text-2xl z-10"
            aria-label="Toggle menu"
          >
            {open ? (
              <X className="w-6 h-6" />
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {open && (
          <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-screen w-full bg-[#8BE784] flex flex-col">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-white transition-all duration-300"
              aria-label="Close menu"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="flex-1 flex flex-col justify-center items-end pr-4">
              <ul className="space-y-4 text-right text-2xl font-bold">
                {navItems.map((item) => (
                  <li key={item.label} className="group">
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => toggleSubmenu(item.label)}
                          className="group relative inline-block text-white hover:underline text-right w-full cursor-pointer"
                        >
                          <span className="inline-block group-hover:opacity-0 transition-opacity">
                            {item.label}
                          </span>
                          <span
                            className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity italic inline-block"
                            style={{
                              fontFamily: '"Times New Roman", Times, serif',
                            }}
                          >
                            {item.label}
                          </span>
                        </button>
                        {expandedSubmenu === item.label && (
                          <ul
                            className="mt-2 space-y-2 text-base rounded-lg p-4 backdrop-blur-sm"
                            style={{ background: "#00000014" }}
                          >
                            {item.submenu.map((subItem) => (
                              <li key={subItem.label}>
                                <a
                                  href={subItem.href}
                                  className="block text-white hover:opacity-80 transition-opacity"
                                  onClick={() => setOpen(false)}
                                >
                                  {subItem.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <a
                        href={item.href}
                        className="group relative inline-block text-white hover:underline cursor-pointer"
                        onClick={() => setOpen(false)}
                      >
                        <span className="inline-block group-hover:opacity-0 transition-opacity">
                          {item.label}
                        </span>
                        <span
                          className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity italic inline-block"
                          style={{
                            fontFamily: '"Times New Roman", Times, serif',
                          }}
                        >
                          {item.label}
                        </span>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
